// Helpers for consuming plan-gate rejections from the B4IGOUS API. Gates live
// on four surfaces with different shapes — see plan-features-frontend.md § 7:
//
//   1. Board create           → HTTP 403, { success: false, message }
//   2. AI prompt endpoints    → HTTP 200, { success: false, intent: "limit reached", aiResponse }
//   3. Invite send (batch)    → HTTP 200, per-email { error: true, message }
//   4. Invite accept          → HTTP 400, { success: false, message }
//   5. DocuSign (10 routes)   → HTTP 403, { success: false, message }
//
// Callers should NOT parse the message string to decide behaviour; they branch
// on the reason returned here and render `message` verbatim as the upgrade
// prompt text (the backend owns the copy, including the current cap like "(3)").

export type PlanGateReason =
  | 'board_limit'
  | 'ai_token_limit'
  | 'guest_invite_limit'
  | 'docusign_disabled'
  | 'other';

export interface PlanGateRejection {
  reason: PlanGateReason;
  message: string;
  // The backend never ships the feature key in the envelope today, but we
  // expose it so callers can branch on the originating endpoint.
  featureKey?: 'boards.limit' | 'ai.tokens.limit' | 'guests.limit' | 'integrations.docusign';
}

/**
 * Extracts a plan-gate rejection from an axios error (or raw response). Returns
 * `null` when the error isn't a plan gate so the caller can fall through to its
 * generic error handling.
 *
 * The `endpoint` hint is what tells us which feature the gate maps to — the
 * envelope itself doesn't name a key.
 */
export function parsePlanGateError(
  err: unknown,
  endpoint: 'board' | 'ai' | 'invite-accept' | 'docusign',
): PlanGateRejection | null {
  // services/index.ts rewraps axios errors as `{ response }`.
  const response =
    (err as { response?: { status?: number; data?: any } } | undefined)?.response;
  if (!response) return null;

  const data = response.data;
  const message = typeof data?.message === 'string' ? data.message : '';

  // Anything with success === true is not a gate rejection.
  if (data?.success === true) return null;

  if (endpoint === 'board' && response.status === 403) {
    return { reason: 'board_limit', message, featureKey: 'boards.limit' };
  }
  if (endpoint === 'docusign' && response.status === 403) {
    return { reason: 'docusign_disabled', message, featureKey: 'integrations.docusign' };
  }
  if (endpoint === 'invite-accept' && response.status === 400) {
    return { reason: 'guest_invite_limit', message, featureKey: 'guests.limit' };
  }
  if (endpoint === 'ai') {
    // AI returns HTTP 200 with success:false + intent:"limit reached" on cap,
    // so most callers will reach this path via the success-response handler,
    // not via axios.catch. Keep the hook here for symmetry.
    if (data?.intent === 'limit reached') {
      return {
        reason: 'ai_token_limit',
        message: typeof data.aiResponse === 'string' ? data.aiResponse : message,
        featureKey: 'ai.tokens.limit',
      };
    }
  }
  return null;
}

/**
 * For the AI endpoints, inspect a *successful* axios response. Returns a
 * rejection when the backend returned a structured "limit reached" payload
 * inside an HTTP 200.
 */
export function parseAiLimitResponse(data: unknown): PlanGateRejection | null {
  if (!data || typeof data !== 'object') return null;
  const body = data as { success?: boolean; intent?: string; aiResponse?: string; message?: string };
  if (body.success === false && body.intent === 'limit reached') {
    return {
      reason: 'ai_token_limit',
      message: typeof body.aiResponse === 'string' ? body.aiResponse : body.message ?? '',
      featureKey: 'ai.tokens.limit',
    };
  }
  return null;
}

/**
 * For the batch invite endpoint. Returns the list of rejected invitees so the
 * caller can render per-email errors inline. The envelope's top-level `success`
 * is always `true` for a syntactically-valid batch, so normal axios catch blocks
 * won't fire — always call this on the successful response.
 */
export interface InviteBatchRejection {
  email: string;
  message: string;
}

export function parseInviteBatchRejections(
  data: unknown,
): InviteBatchRejection[] {
  if (!data || typeof data !== 'object') return [];
  const map = (data as { data?: Record<string, { error?: boolean; message?: string }> }).data;
  if (!map || typeof map !== 'object') return [];
  return Object.entries(map)
    .filter(([, v]) => v && v.error === true)
    .map(([email, v]) => ({ email, message: typeof v.message === 'string' ? v.message : '' }));
}

/**
 * Reads a boolean feature flag off the logged-in user's subscribed plan. The
 * plan catalog lives at `currentUser.data.user.subscription.plan.features`
 * (keyed by the backend feature catalog — see plans.ts). Returns `false` when
 * the user is unauthenticated, has no subscription, or the plan omits the key.
 *
 * Use for pre-gate UX (hide menu entries, disable actions) — the server still
 * owns the authoritative 403/400 gate.
 */
export function hasPlanFeature(
  currentUser: unknown,
  key: 'integrations.docusign',
): boolean {
  const features = (currentUser as any)?.data?.user?.subscription?.plan?.features;
  return features?.[key] === true;
}
